import { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import SEO from '@/components/SEO';

const API = `/api`;

const contactInfo = [
  { icon: Phone, label: 'Phone', value: '+61 469 362 319', href: 'tel:+61469362319' },
  { icon: Mail, label: 'Email', value: 'info@vulcanrecruitment.com', href: 'mailto:info@vulcanrecruitment.com' },
  { icon: MapPin, label: 'Location', value: 'Thomastown, Victoria, Australia', href: null },
];

const businessHours = [
  { day: 'Monday - Friday', hours: '8:00 AM - 6:00 PM' },
  { day: 'Saturday', hours: 'By Appointment' },
  { day: 'Sunday', hours: 'Closed' },
];

export default function Contact() {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      toast.success(response.data.message);
      setFormData({
        full_name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO page="contact" />
      {/* Hero */}
      <section className="pt-32 pb-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <p className="text-[#F5A623] font-semibold uppercase tracking-wider mb-4">Contact Us</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white uppercase mb-6">
              Let's Talk About Your Hiring Needs
            </h1>
            <p className="text-slate-300 text-lg leading-relaxed">
              Whether you're looking to hire or seeking your next opportunity, we're here to help. Get in touch with our team today.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <div className="bg-white p-8 border border-slate-200 mb-8">
                <h2 className="font-heading text-2xl font-bold text-slate-900 uppercase mb-6">Get In Touch</h2>
                <div className="space-y-6">
                  {contactInfo.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-teal-50 flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-5 h-5 text-[#1A8B9D]" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-700 text-sm uppercase tracking-wide">{item.label}</p>
                        {item.href ? (
                          <a 
                            href={item.href} 
                            className="text-slate-900 hover:text-[#1A8B9D] transition-colors"
                            data-testid={`contact-${item.label.toLowerCase()}`}
                          >
                            {item.value}
                          </a>
                        ) : (
                          <p className="text-slate-900">{item.value}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Business Hours */}
              <div className="bg-white p-8 border border-slate-200 mb-8">
                <div className="flex items-center gap-3 mb-6">
                  <Clock className="w-5 h-5 text-[#1A8B9D]" />
                  <h3 className="font-heading text-xl font-bold text-slate-900 uppercase">Business Hours</h3>
                </div>
                <div className="space-y-3">
                  {businessHours.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span className="text-slate-600">{item.day}</span>
                      <span className="text-slate-900 font-medium">{item.hours}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Map Placeholder */}
              <div className="bg-slate-200 h-[200px] relative overflow-hidden">
                <div className="absolute inset-0 flex items-center justify-center bg-slate-300">
                  <div className="text-center">
                    <MapPin className="w-8 h-8 text-[#1A8B9D] mx-auto mb-2" />
                    <p className="text-slate-600 text-sm font-medium">Thomastown, VIC</p>
                    <p className="text-slate-500 text-xs">Melbourne, Australia</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <motion.form
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                onSubmit={handleSubmit}
                className="bg-white p-8 md:p-12 border border-slate-200"
                data-testid="contact-form"
              >
                <h2 className="font-heading text-2xl font-bold text-slate-900 uppercase mb-8">Send Us A Message</h2>
                
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Full Name *</label>
                    <Input
                      type="text"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleChange}
                      required
                      className="h-12 border-slate-300 focus:border-[#1A8B9D]"
                      placeholder="Your full name"
                      data-testid="contact-form-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Email *</label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="h-12 border-slate-300 focus:border-[#1A8B9D]"
                      placeholder="your@email.com"
                      data-testid="contact-form-email"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Phone *</label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="h-12 border-slate-300 focus:border-[#1A8B9D]"
                      placeholder="+61 xxx xxx xxx"
                      data-testid="contact-form-phone"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Subject *</label>
                    <Input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="h-12 border-slate-300 focus:border-[#1A8B9D]"
                      placeholder="How can we help?"
                      data-testid="contact-form-subject"
                    />
                  </div>
                </div>
                <div className="mb-8">
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Message *</label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    className="min-h-[150px] border-slate-300 focus:border-[#1A8B9D]"
                    placeholder="Tell us more about your enquiry..."
                    data-testid="contact-form-message"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#1A8B9D] hover:bg-[#115E6B] text-white font-bold py-4 uppercase tracking-wide text-base"
                  data-testid="contact-form-submit"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </motion.form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
